/**
 * Utility functions for search functionality
 * Handles both static routes and dynamic content search
 */

import type { VersionStates } from '@common/Providers/GraphqlProvider/generated';

// Define static routes with their corresponding labels
export interface StaticRoute {
  label: string;
  route: string;
  category: string;
  keywords: string[];
}

// Static routes configuration with Spanish labels and categories
export const STATIC_ROUTES: StaticRoute[] = [
  // ========== ADMINISTRACIÓN DE VERSIONES ==========
  {
    label: 'Versión de Página',
    route: '/admin/home/admin-version-page/version-page',
    category: 'Administración de versiones',
    keywords: ['version', 'pagina', 'page', 'admin'],
  },

  // ========== CONFIGURACIÓN DE LA PÁGINA PRINCIPAL ==========
  {
    label: 'Configuración de Objetos de Cabecera',
    route: '/admin/home/page-config/header-objects',
    category: 'Configuración de la página principal',
    keywords: ['cabecera', 'header', 'objetos', 'configuracion', 'paginas'],
  },
  {
    label: 'Configuración de Objetos de Pie de Página',
    route: '/admin/home/page-config/footer-objects',
    category: 'Configuración de la página principal',
    keywords: ['pie', 'footer', 'objetos', 'configuracion', 'paginas'],
  },
  {
    label: 'Configuración de Página Principal',
    route: '/admin/home/page-config/home-page',
    category: 'Configuración de la página principal',
    keywords: ['home', 'inicio', 'principal', 'pagina', 'paginas'],
  },
  {
    label: 'Pagina de Transparencia',
    route: '/admin/home/micro-sites/default-sites/transparency-page',
    category: 'Configuración de la página principal',
    keywords: ['transparencia', 'transparency', 'paginas'],
  },
  {
    label: 'Pagina de Sala de Prensa',
    route: '/admin/home/page-config/press-room',
    category: 'Configuración de la página principal',
    keywords: ['prensa', 'press', 'sala', 'noticias', 'paginas'],
  },
  {
    label: 'Pagina de Programas Institucionales',
    route: '/admin/home/page-config/institutional-programs',
    category: 'Configuración de la página principal',
    keywords: ['programas', 'institucionales', 'programs', 'paginas'],
  },
  {
    label: 'Pagina de Tramites',
    route: '/admin/home/page-config/services-page',
    category: 'Configuración de la página principal',
    keywords: ['servicios', 'services', 'tramites', 'paginas'],
  },
  {
    label: 'Pagina de participa',
    route: '/admin/home/page-config/participates-page',
    category: 'Configuración de la página principal',
    keywords: ['participacion', 'participate', 'participa', 'paginas'],
  },
  {
    label: 'Configuración de Descargas',
    route: '/admin/home/page-config/downloadable',
    category: 'Configuración de la página principal',
    keywords: ['descargas', 'downloadable', 'archivos', 'paginas'],
  },
  {
    label: 'Pagina de Normativas',
    route: '/admin/home/page-config/regulations',
    category: 'Configuración de la página principal',
    keywords: ['normativas', 'regulations', 'normas', 'reglamentos', 'paginas'],
  },
  {
    label: 'Usuarios de Cuenta (Configuración)',
    route: '/admin/home/page-config/account-users',
    category: 'Configuración de la página principal',
    keywords: ['usuarios', 'cuenta', 'account', 'users', 'paginas'],
  },

  // ========== ADMINISTRADOR DE CONTENIDO ==========
  {
    label: 'Documentos',
    route: '/admin/home/content-management/documents',
    category: 'Administrador de contenido',
    keywords: ['documentos', 'documents', 'archivos', 'files'],
  },
  {
    label: 'Gestión de Trámites y Servicios',
    route: '/admin/home/content-management/procedures-and-services',
    category: 'Administrador de contenido',
    keywords: ['tramites', 'servicios', 'procedures', 'services'],
  },
  {
    label: 'Gestión de Normativas',
    route: '/admin/home/content-management/regulation',
    category: 'Administrador de contenido',
    keywords: ['normativas', 'regulation', 'normas'],
  },
  {
    label: 'Gestión de Noticias',
    route: '/admin/home/content-management/news-management',
    category: 'Administrador de contenido',
    keywords: ['noticias', 'news', 'prensa', 'press'],
  },
  {
    label: 'Gestión de Eventos',
    route: '/admin/home/content-management/events',
    category: 'Administrador de contenido',
    keywords: ['eventos', 'events'],
  },
  {
    label: 'Gestión de Programas Institucionales',
    route: '/admin/home/content-management/institutional-programs-management',
    category: 'Administrador de contenido',
    keywords: ['programas', 'institucionales', 'programs', 'management'],
  },
  {
    label: 'Gestión de PQRSD',
    route: '/admin/home/content-management/pqrsd',
    category: 'Administrador de contenido',
    keywords: ['pqrsd', 'peticiones', 'quejas', 'reclamos'],
  },
  {
    label: 'Sala de Prensa (Gestión)',
    route: '/admin/home/content-management/press-room',
    category: 'Administrador de contenido',
    keywords: ['prensa', 'press', 'room', 'sala'],
  },
  {
    label: 'Nueva Regulación Participación',
    route: '/admin/home/content-management/participate/new-regulation',
    category: 'Administrador de contenido',
    keywords: ['nueva', 'regulacion', 'participacion', 'crear'],
  },
  {
    label: 'Nueva Participación',
    route: '/admin/home/content-management/participate/new',
    category: 'Administrador de contenido',
    keywords: ['nueva', 'participacion', 'crear', 'new'],
  },
  {
    label: 'Nueva Regulación',
    route: '/admin/home/content-management/regulation/new',
    category: 'Administrador de contenido',
    keywords: ['nueva', 'regulacion', 'crear', 'new'],
  },
  {
    label: 'Nueva Página Dinámica',
    route: '/admin/home/content-management/dynamic-pages/new-page',
    category: 'Administrador de contenido',
    keywords: ['nueva', 'pagina', 'dinamica', 'crear', 'paginas'],
  },
  {
    label: 'Nuevo PQRSD',
    route: '/admin/home/content-management/pqrsd/new',
    category: 'Administrador de contenido',
    keywords: ['nuevo', 'pqrsd', 'crear', 'peticion'],
  },
  {
    label: 'Gestión de Cuenta',
    route: '/admin/home/manage-account',
    category: 'Administrador de contenido',
    keywords: ['cuenta', 'account', 'perfil', 'profile'],
  },
  {
    label: 'PQRSD Administración de contenido',
    route: '/admin/home/pqrsd',
    category: 'Administrador de contenido',
    keywords: ['pqrsd', 'peticiones', 'quejas', 'reclamos', 'admin'],
  },
  {
    label: 'Gestión de Contenido (Legacy)',
    route: '/admin/home/managment-content/press-room-managment',
    category: 'Administrador de contenido',
    keywords: ['managment', 'content', 'legacy', 'prensa'],
  },

  // ========== MICROSITIOS ==========
  {
    label: 'Sitios Predeterminados',
    route: '/admin/home/micro-sites/default-sites',
    category: 'Micrositios',
    keywords: ['sitios', 'predeterminados', 'defecto', 'default', 'sites'],
  },
  {
    label: 'Sitios Personalizados',
    route: '/admin/home/micro-sites/custom-sites',
    category: 'Micrositios',
    keywords: ['sitios', 'personalizados', 'custom', 'sites'],
  },
  {
    label: 'Crear sitio personalizado',
    route: '/admin/home/micro-sites/custom-sites/new',
    category: 'Micrositios',
    keywords: ['crear', 'sitio', 'personalizado', 'nuevo', 'custom', 'new'],
  },
  {
    label: 'Páginas Dinámicas (Micrositios)',
    route: '/admin/home/micro-sites/dynamic-page',
    category: 'Micrositios',
    keywords: ['paginas', 'dinamicas', 'dynamic', 'micrositios'],
  },
  {
    label: 'Crear pagina de navegacion',
    route: '/admin/home/micro-sites/dynamic-page/new',
    category: 'Micrositios',
    keywords: ['crear', 'pagina', 'navegacion', 'nueva', 'dynamic', 'new', 'paginas'],
  },
  {
    label: 'Navegación de Páginas',
    route: '/admin/home/parametrization/micro-sites',
    category: 'Micrositios',
    keywords: ['navegacion', 'navigation', 'menu', 'paginas'],
  },

  // ========== PARAMETRIZACIÓN ==========
  {
    label: 'Parametrización',
    route: '/admin/home/parametrization',
    category: 'Parametrización',
    keywords: ['parametrización', 'parametrization', 'configuración', 'ajustes'],
  },
  {
    label: 'Gestión de Dependencias',
    route: '/admin/home/parametrization/dependencies',
    category: 'Parametrización',
    keywords: ['dependencias', 'dependencies'],
  },
  {
    label: 'Nuevo Dependencia',
    route: '/admin/home/parametrization/dependencies/new',
    category: 'Parametrización',
    keywords: ['nueva', 'dependencia', 'crear', 'new'],
  },
  {
    label: 'Parámetros del Sistema',
    route: '/admin/home/parametrization/parameters',
    category: 'Parametrización',
    keywords: ['parametros', 'parameters', 'sistema', 'configuracion'],
  },
  {
    label: 'Gestión de Parámetros del Sistema',
    route: '/admin/home/parametrization/parameters',
    category: 'Parametrización',
    keywords: ['parametros', 'parameters', 'sistema', 'system'],
  },
  {
    label: 'Nuevo Parámetro del Sistema',
    route: '/admin/home/parametrization/parameters/new',
    category: 'Parametrización',
    keywords: ['nuevo', 'parametro', 'sistema', 'crear'],
  },
  {
    label: 'Gestión de Temas',
    route: '/admin/home/parametrization/themes',
    category: 'Parametrización',
    keywords: ['temas', 'themes', 'colores', 'estilo'],
  },
  {
    label: 'Micrositios (Parametrización)',
    route: '/admin/home/parametrization/micro-sites',
    category: 'Parametrización',
    keywords: ['micrositios', 'micro', 'sites', 'parametrization'],
  },
  {
    label: 'Gestión de Micrositios Parametrización',
    route: '/admin/home/parametrization/micro-sites',
    category: 'Parametrización',
    keywords: ['micrositios', 'micro', 'sites', 'parametrizacion'],
  },
  {
    label: 'Nuevo Micrositio',
    route: '/admin/home/parametrization/micro-sites/new',
    category: 'Parametrización',
    keywords: ['nuevo', 'micrositio', 'crear', 'new'],
  },
  {
    label: 'Editar Micrositio',
    route: '/admin/home/parametrization/micro-sites/edit',
    category: 'Parametrización',
    keywords: ['editar', 'micrositio', 'modificar', 'edit'],
  },
  {
    label: 'Listas Dinámicas',
    route: '/admin/home/parametrization/dynamic-list',
    category: 'Parametrización',
    keywords: ['listas', 'dinamicas', 'dynamic', 'list', 'parametrization'],
  },
  {
    label: 'Modulo de seguridad del sistema',
    route: '/admin/home/parametrization/security',
    category: 'Parametrización',
    keywords: ['seguridad', 'security', 'usuarios', 'roles', 'modulo', 'sistema'],
  },
  {
    label: 'Usuarios del Sistema (Parametrización)',
    route: '/admin/home/parametrization/security/account-users',
    category: 'Parametrización',
    keywords: ['usuarios', 'cuentas', 'accounts', 'users', 'parametrization'],
  },
  {
    label: 'Usuarios Públicos',
    route: '/admin/home/parametrization/security/account-users',
    category: 'Parametrización',
    keywords: ['usuarios', 'publicos', 'users', 'cuentas', 'accounts'],
  },
  {
    label: 'Nuevo Usuario',
    route: '/admin/home/parametrization/security/account-users/new-user',
    category: 'Parametrización',
    keywords: ['nuevo', 'usuario', 'crear', 'user'],
  },
  {
    label: 'Usuarios Administrativos',
    route: '/admin/home/parametrization/security/account-users-admin',
    category: 'Parametrización',
    keywords: ['usuarios', 'administrativos', 'admin', 'administradores', 'accounts'],
  },
  {
    label: 'Gestión de Roles',
    route: '/admin/home/parametrization/security/role-management',
    category: 'Parametrización',
    keywords: ['roles', 'permisos', 'permissions'],
  },
  {
    label: 'Nuevo Rol',
    route: '/admin/home/parametrization/security/role-management/new-role',
    category: 'Parametrización',
    keywords: ['nuevo', 'rol', 'crear', 'role'],
  },
  {
    label: 'Auditoria del Sistema',
    route: '/admin/home/parametrization/security/system-audit',
    category: 'Parametrización',
    keywords: ['auditoria', 'audit', 'logs', 'sistema', 'seguimiento'],
  },

  // ========== RUTAS PÚBLICAS ==========
  {
    label: 'Participación Ciudadana',
    route: '/participate',
    category: 'Público',
    keywords: ['participacion', 'participate', 'ciudadana', 'participa'],
  },
  {
    label: 'PQRS',
    route: '/pqrs',
    category: 'Público',
    keywords: ['pqrs', 'peticiones', 'quejas', 'reclamos', 'sugerencias'],
  },
];

// Search function for static routes
export function searchStaticRoutes(searchTerm: string): StaticRoute[] {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return STATIC_ROUTES;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  return STATIC_ROUTES.filter((route) => {
    // Search in label
    if (route.label.toLowerCase().includes(normalizedSearchTerm)) {
      return true;
    }

    // Search in category
    if (route.category.toLowerCase().includes(normalizedSearchTerm)) {
      return true;
    }

    // Search in keywords
    if (route.keywords.some((keyword) => keyword.toLowerCase().includes(normalizedSearchTerm))) {
      return true;
    }

    // Search in route path
    if (route.route.toLowerCase().includes(normalizedSearchTerm)) {
      return true;
    }

    return false;
  });
}

// Interface for dynamic content search results
export interface DynamicContent {
  id: string;
  title: string;
  type: string;
  category: string;
  url: string;
  description?: string;
  publishedAt?: string;
  status?: VersionStates | undefined;
}

// Dynamic content search using API service
// Esta función ha sido movida al hook useSearchContent que maneja GraphQL directamente
// export async function searchDynamicContent(searchTerm: string): Promise<DynamicContent[]> {
//   // Esta funcionalidad ahora está en useSearchContent hook
//   return [];
// }

// Combined search function that returns both static and dynamic results
export interface SearchResults {
  static: StaticRoute[];
  dynamic: DynamicContent[];
}

// Esta función ha sido reemplazada por el hook useSearchContent
// export async function performSearch(searchTerm: string): Promise<SearchResults> {
//   // La funcionalidad completa está ahora en useSearchContent hook
//   const staticResults = searchStaticRoutes(searchTerm);
//   return {
//     static: staticResults,
//     dynamic: [], // Manejado por useSearchContent
//   };
// }

// Helper function to group results by category
export function groupResultsByCategory(results: SearchResults): Record<string, any[]> {
  const grouped: Record<string, any[]> = {};

  // Group static results
  results.static.forEach((route) => {
    const category = `Paginas - ${route.category}`;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push({
      ...route,
      type: 'static',
    });
  });

  // Group dynamic results
  results.dynamic.forEach((content) => {
    const category = `Contenido - ${content.category}`;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push({
      ...content,
      type: 'dynamic',
    });
  });

  return grouped;
}

// Type for the final search result item
export interface SearchResultItem {
  id?: string;
  label?: string;
  title?: string;
  route?: string;
  url?: string;
  category: string;
  type: 'static' | 'dynamic';
  excerpt?: string;
  publishedAt?: string;
  status?: string;
  keywords?: string[];
}
